"use client";
import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";

import comCSS from "./NewsletterSubscription.module.scss";
import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";
import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";
import NewsletterForm from "@/next-src/components/home/newsletter-section/newsletter-form/NewsletterForm";
import Calendly from "@/next-src/components/ui/calendly/Calendly";

const NewsletterSubscription = ({ data, className, pageId = "homepage" }) => {
  const { width: windowWidth } = useWindowDimensions();
  const {
    preTitleMobile,
    titleMobile,
    subTitleMobile,
    textMobile,
    form,
    bgColor,
    bgColorMobile,
    successMsgMobile,
  } = data || {};

  let { title, preTitle, text, subTitle, successMsg } = data || {};

  if (windowWidth < 992) {
    preTitle = preTitleMobile;
    title = titleMobile;
    text = textMobile;
    subTitle = subTitleMobile;
    successMsg = successMsgMobile;
  }

  return (
    <>
      <section
        className={cn(
          className,
          comCSS.wrapper,
          "section background-color-soft-blue padding-section-large"
        )}
        style={{
          "--section-bg-color-xs": bgColorMobile,
          "--section-bg-color-lg": bgColor,
        }}
      >
        <div className="padding-global">
          <div className="container-large">
            <div
              id="newsletterSubscriptionSection"
              className={cn(comCSS.scrollToBar)}
            />
            <div className="margin-bottom margin-xsmall">
              <div className="text-note">{convertHTMLtoJSX(preTitle)}</div>
            </div>

            {title ? (
              <div className={cn("margin-bottom margin-xsmall")}>
                {convertHTMLtoJSX(title)}
              </div>
            ) : null}

            {subTitle ? (
              <div className={cn("margin-bottom margin-xsmall")}>
                {convertHTMLtoJSX(subTitle)}
              </div>
            ) : null}

            {form.mode === "calendly" ? (
              <div className={comCSS.btnWrapper}>
                <Calendly
                  classNameBtn={cn(comCSS.btn)}
                  data={{
                    calendlyButton: form.submitButton,
                    calendlyButtonMobile: form.submitButtonMobile,
                    calendlyButtonSuccess: form.buttonSuccess,
                    calendlyButtonSuccessMobile: form.buttonSuccessMobile,
                  }}
                />
              </div>
            ) : (
              <NewsletterForm data={form} pageId={pageId} />
            )}

            <div className={comCSS.contentTextWrapper}>
              {text ? (
                <div className={cn("margin-bottom margin-xsmall")}>
                  {convertHTMLtoJSX(text)}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NewsletterSubscription;

const FormFieldType = PropTypes.shape({
  placeholder: PropTypes.string, // First name placeholder (optional)
  isRequired: PropTypes.bool, // Indicates if first name is required (optional)
  hide: PropTypes.bool, // Indicates if first name field is hidden (optional)
  label: PropTypes.string, // First name label (optional)
});

NewsletterSubscription.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string.isRequired, // Main title (consider removing HTML tags)
    titleMobile: PropTypes.string.isRequired, // Mobile title (consider removing HTML tags)
    subTitle: PropTypes.string, // Subtitle (optional)
    subTitleMobile: PropTypes.string, // Mobile subtitle (optional)
    form: PropTypes.shape({
      mode: PropTypes.string, // Form mode (optional)
      firstname: FormFieldType,
      lastname: FormFieldType,
      email: FormFieldType,
      cityOfResidence: FormFieldType,
      questionnaire: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string, // Questionnaire item ID (optional)
          placeholder: PropTypes.string, // Questionnaire item placeholder (optional)
          isRequired: PropTypes.bool, // Indicates if questionnaire item is required (optional)
          hide: PropTypes.bool, // Indicates if questionnaire item is hidden (optional)
          label: PropTypes.string, // Questionnaire item label (optional)
        })
      ),
      successMsg: PropTypes.string, // Success message (optional)
      successMsgMobile: PropTypes.string, // Mobile success message (optional)
      submitButton: PropTypes.shape({
        id: PropTypes.string, // Submit button ID (optional)
        text: PropTypes.string.isRequired, // Submit button text (consider removing HTML tags)
        url: PropTypes.string, // Submit button URL (optional)
        bgColor: PropTypes.string, // Submit button background color (optional)
        bgColorHover: PropTypes.string, // Submit button hover background color (optional)
        borderColor: PropTypes.string, // Submit button border color (optional)
        borderColorHover: PropTypes.string, // Submit button hover border color (optional)
        hide: PropTypes.bool, // Indicates if submit button is hidden (optional)
        action: PropTypes.string, // Submit button action (optional)
        calendlyLink: PropTypes.string, // Calendly link (optional)
      }),
      submitButtonMobile: PropTypes.shape({
        // Similar structure to submitButton
        id: PropTypes.string, // Submit button ID (optional)
        text: PropTypes.string.isRequired, // Submit button text (consider removing HTML tags)
        url: PropTypes.string, // Submit button URL (optional)
        bgColor: PropTypes.string, // Submit button background color (optional)
        bgColorHover: PropTypes.string, // Submit button hover background color (optional)
        borderColor: PropTypes.string, // Submit button border color (optional)
        borderColorHover: PropTypes.string, // Submit button hover border color (optional)
        hide: PropTypes.bool, // Indicates if submit button is hidden (optional)
        action: PropTypes.string, // Submit button action (optional)
        calendlyLink: PropTypes.string, // Calendly link (optional)
      }),
      buttonSubmiting: PropTypes.shape({
        // Similar structure to submitButton
        id: PropTypes.string, // Submit button ID (optional)
        text: PropTypes.string.isRequired, // Submit button text (consider removing HTML tags)
        url: PropTypes.string, // Submit button URL (optional)
        bgColor: PropTypes.string, // Submit button background color (optional)
        bgColorHover: PropTypes.string, // Submit button hover background color (optional)
        borderColor: PropTypes.string, // Submit button border color (optional)
        borderColorHover: PropTypes.string, // Submit button hover border color (optional)
        hide: PropTypes.bool, // Indicates if submit button is hidden (optional)
        action: PropTypes.string, // Submit button action (optional)
        calendlyLink: PropTypes.string, // Calendly link (optional)
      }),
      buttonSubmitingMobile: PropTypes.shape({
        // Similar structure to submitButton
        id: PropTypes.string, // Submit button ID (optional)
        text: PropTypes.string.isRequired, // Submit button text (consider removing HTML tags)
        url: PropTypes.string, // Submit button URL (optional)
        bgColor: PropTypes.string, // Submit button background color (optional)
        bgColorHover: PropTypes.string, // Submit button hover background color (optional)
        borderColor: PropTypes.string, // Submit button border color (optional)
        borderColorHover: PropTypes.string, // Submit button hover border color (optional)
        hide: PropTypes.bool, // Indicates if submit button is hidden (optional)
        action: PropTypes.string, // Submit button action (optional)
        calendlyLink: PropTypes.string, // Calendly link (optional)
      }),
      buttonSuccess: PropTypes.shape({
        // Similar structure to submitButton
        id: PropTypes.string, // Submit button ID (optional)
        text: PropTypes.string.isRequired, // Submit button text (consider removing HTML tags)
        url: PropTypes.string, // Submit button URL (optional)
        bgColor: PropTypes.string, // Submit button background color (optional)
        bgColorHover: PropTypes.string, // Submit button hover background color (optional)
        borderColor: PropTypes.string, // Submit button border color (optional)
        borderColorHover: PropTypes.string, // Submit button hover border color (optional)
        hide: PropTypes.bool, // Indicates if submit button is hidden (optional)
        action: PropTypes.string, // Submit button action (optional)
        calendlyLink: PropTypes.string, // Calendly link (optional)
      }),
      buttonSuccessMobile: PropTypes.shape({
        // Similar structure to submitButton
        id: PropTypes.string, // Submit button ID (optional)
        text: PropTypes.string.isRequired, // Submit button text (consider removing HTML tags)
        url: PropTypes.string, // Submit button URL (optional)
        bgColor: PropTypes.string, // Submit button background color (optional)
        bgColorHover: PropTypes.string, // Submit button hover background color (optional)
        borderColor: PropTypes.string, // Submit button border color (optional)
        borderColorHover: PropTypes.string, // Submit button hover border color (optional)
        hide: PropTypes.bool, // Indicates if submit button is hidden (optional)
        action: PropTypes.string, // Submit button action (optional)
        calendlyLink: PropTypes.string, // Calendly link (optional)
      }),
    }),
    bgColor: PropTypes.string, // Background color (optional)
    bgColorMobile: PropTypes.string, // Mobile background color (optional)
    successMsg: PropTypes.string, // Success message (optional)
    successMsgMobile: PropTypes.string, // Mobile success message (optional)
  }).isRequired,
  pageId: PropTypes.string.isRequired,
};
